// src/pages/Leads/CreateLead.js
import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  Grid,
  Autocomplete,
  Skeleton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import { useDispatch } from 'react-redux';
import {
  getLeadsStatus,
  getLeadSources,
  getLeadBrands,
  createLead,
  getLeadById,
  updateLead,
  getLeadAssignedUsers,
} from '../../store/slices/leadsSlice';
import { extractList } from '../../utils/response';
import { useAuth } from '../../hooks/useAuth';
import api from '../../api/config';
import { LOOKUP, USERS } from '../../api/endpoints';
import LeadAdditionalDetailsSection from '../../components/Leads/LeadAdditionalDetailsSection';
import {
  findUnitedStatesId,
  getCachedCountries,
  setCachedCountries,
  setCachedUsCountryId,
} from '../../utils/lookupSessionCache';
import {
  createEmptyOfferedService,
  normalizeOfferedServices,
  serializeOfferedServices,
} from '../../components/Leads/leadFormHelpers';
import { isCloserUser } from '../../components/Leads/utils';

/** `datetime-local` value from API datetime string */
function toDatetimeLocalValue(value) {
  if (value == null || value === '') return '';
  const d = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function normalizeStatusName(name) {
  return (name || '').toLowerCase().replace(/[\s_-]+/g, ' ').trim();
}

function isFollowUpStatusSelected(statusOptions, statusId) {
  if (!statusId) return false;
  const status = statusOptions.find(
    (s) => s.id === statusId || String(s.id) === String(statusId)
  );
  const normalized = normalizeStatusName(status?.name);
  return normalized.includes('follow up') || normalized.startsWith('followup') || normalized === 'new lead';
}

function findFollowUpCStatus(statusOptions) {
  if (!Array.isArray(statusOptions)) return null;
  return (
    statusOptions.find((s) => {
      const n = normalizeStatusName(s?.name);
      return n === 'follow up (c)' || n === 'follow c';
    }) ?? null
  );
}

function isLiveTransferStatusSelected(statusOptions, statusId) {
  if (!statusId) return false;
  const status = statusOptions.find(
    (s) => s.id === statusId || String(s.id) === String(statusId)
  );
  const normalized = normalizeStatusName(status?.name);
  return normalized.includes('live transfer');
}

function toBool(v) {
  if (typeof v === 'string') return ['1', 'true', 'yes'].includes(v.toLowerCase().trim());
  return v === true || v === 1;
}

function extractLeadAttachmentIds(leadData) {
  if (Array.isArray(leadData.attachments) && leadData.attachments.length > 0) {
    return leadData.attachments
      .map((a) => (a && typeof a === 'object' ? a.id : a))
      .filter(Boolean);
  }
  if (Array.isArray(leadData.attachment_ids)) {
    return leadData.attachment_ids.filter(Boolean);
  }
  return [];
}

/** Maps API lead (nested or legacy flat) into CreateLead form state */
function mapLeadApiToForm(leadData) {
  const dba = leadData.dba && typeof leadData.dba === 'object' ? leadData.dba : {};
  // Backend key is actually "legalIn_formation" (typo) — holds legal AND most dba/business fields.
  const li =
    leadData.legalIn_formation && typeof leadData.legalIn_formation === 'object'
      ? leadData.legalIn_formation
      : {};
  const legal =
    (leadData.legal_information && typeof leadData.legal_information === 'object' ? leadData.legal_information : null) ||
    (dba.legal_information && typeof dba.legal_information === 'object' ? dba.legal_information : null) ||
    li;
  const merchant =
    (leadData.merchant && typeof leadData.merchant === 'object' ? leadData.merchant : null) ||
    (dba.merchant && typeof dba.merchant === 'object' ? dba.merchant : {});
  const bank =
    (leadData.bank_account && typeof leadData.bank_account === 'object' ? leadData.bank_account : null) ||
    (dba.bank_account && typeof dba.bank_account === 'object' ? dba.bank_account : null) ||
    (dba.bank && typeof dba.bank === 'object' ? dba.bank : {});
  const rawEquip =
    (Array.isArray(leadData.equipment) && leadData.equipment.length ? leadData.equipment : null) ||
    (Array.isArray(leadData.equipments) && leadData.equipments.length ? leadData.equipments : null) ||
    (Array.isArray(dba.equipments) && dba.equipments.length ? dba.equipments : null) ||
    (Array.isArray(li.equipments) && li.equipments.length ? li.equipments : []);
  const eq0 = rawEquip.length ? rawEquip[0] : {};
  const resolveId = (v) => (v && typeof v === 'object' ? v.id : v) || '';

  const submission = leadData.submission_date ?? dba.submission_date ?? li.submission_date;

  return {
    name: leadData.name || '',
    email: leadData.email || '',
    phone: leadData.phone || '',
    address: leadData.address || '',
    description: leadData.description || '',
    status_id: leadData.last_status_log?.status?.id || '',
    source_id: leadData.source?.id || '',
    brand_id: leadData.brand?.id || '',
    country_id: leadData.country?.id || '',
    submission_date: submission ? String(submission).slice(0, 10) : '',
    legal_business_name: leadData.legal_business_name ?? legal.name ?? '',
    dba_name: leadData.dba_name ?? dba.dba_name ?? dba.name ?? '',
    db_name: leadData.db_name ?? dba.db_name ?? '',
    federal_tax_id: leadData.federal_tax_id ?? merchant.federal_tax_id ?? '',
    ssn: leadData.ssn ?? dba.ssn ?? li.ssn ?? '',
    ein: leadData.ein ?? dba.ein ?? li.ein ?? '',
    business_years: leadData.business_years ?? merchant.business_years ?? '',
    business_months: leadData.business_months ?? merchant.business_months ?? '',
    business_category: leadData.business_category ?? merchant.business_category ?? '',
    monthly_volume: leadData.monthly_volume ?? dba.monthly_volume ?? li.monthly_volume ?? '',
    equipment_type_id: resolveId(leadData.equipment_type_id || eq0.equipment_type_id || eq0.equipment_type),
    manufacturer_id: resolveId(leadData.manufacturer_id || eq0.manufacturer_id || eq0.manufacturer),
    equipment_model_id: resolveId(leadData.equipment_model_id || eq0.equipment_model_id || eq0.equipment_model),
    personal_phone: leadData.personal_phone ?? dba.phone ?? '',
    legal_business_phone: leadData.legal_business_phone ?? legal.phone ?? '',
    legal_business_address: leadData.legal_business_address ?? legal.address ?? '',
    legal_business_email: leadData.legal_business_email ?? legal.email ?? '',
    monthly_lease: leadData.monthly_lease ?? dba.monthly_lease ?? li.monthly_lease ?? '',
    is_bundled: toBool(leadData.is_bundled ?? dba.is_bundled ?? li.is_bundled),
    processing_offered: leadData.processing_offered ?? dba.processing_offered ?? li.processing_offered ?? '',
    avg_month_sales: leadData.avg_month_sales ?? merchant.avg_month_sales ?? '',
    highest_ticket: leadData.highest_ticket ?? dba.highest_ticket ?? li.highest_ticket ?? '',
    average_tickets:
      leadData.average_tickets ?? dba.average_ticket ?? merchant.average_ticket ?? li.average_ticket ?? '',
    bank_name: leadData.bank_name ?? bank.bank_name ?? '',
    routing_number: leadData.routing_number ?? bank.routing_number ?? '',
    account_number: leadData.account_number ?? bank.account_number ?? '',
    fns_number_for_ebt: leadData.fns_number_for_ebt ?? dba.fns_number_for_ebt ?? li.fns_number_for_ebt ?? '',
    dba_address: leadData.dba_address ?? dba.address ?? '',
    additional_note: leadData.additional_note ?? dba.additional_note ?? '',
    lease_id: resolveId(leadData.lease_id || leadData.lease || eq0.lease_id || eq0.lease),
    merchant_service_provider_id: resolveId(
      leadData.merchant_service_provider_id ||
        leadData.merchant_service_provider ||
        eq0.merchant_service_provider_id ||
        eq0.merchant_service_provider
    ),
    follow_up_date_time: toDatetimeLocalValue(
      leadData.follow_up_date_time ?? leadData.last_status_log?.date
    ),
    closer_uuid: leadData.closer?.uuid ?? leadData.closer_uuid ?? '',
    offer_services: normalizeOfferedServices(
      leadData.offer_services ?? dba.offer_services ?? []
    ),
  };
}

function putNum(target, key, val) {
  if (val === '' || val === undefined || val === null) return;
  const n = Number(val);
  if (!Number.isNaN(n)) target[key] = n;
}

/**
 * Backend expects nested groups (same shape as dba[field], legal_information[field], etc.).
 * Attachments are sent as `attachments`: [id, ...] (attachments[0], attachments[1], …).
 */
function buildLeadSubmitPayload(formData, statusOptions = []) {
  const base = {
    dba_name: formData.dba_name,
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    address: formData.address,
    description: formData.description,
    status_id: formData.status_id,
    source_id: formData.source_id,
    brand_id: formData.brand_id,
    ...(formData.country_id ? { country_id: formData.country_id } : {}),
    ...(formData.import_business_id ? { import_business_id: formData.import_business_id } : {}),
    assignees: formData.assignee_ids,
  };

  const dba = {};
  if (formData.submission_date) dba.submission_date = formData.submission_date;
  if (formData.personal_phone) dba.phone = formData.personal_phone;
  if (formData.dba_name) dba.dba_name = formData.dba_name;
  if (formData.db_name) dba.db_name = formData.db_name;
  if (formData.monthly_lease !== '' && formData.monthly_lease != null) dba.monthly_lease = formData.monthly_lease;
  if (formData.is_bundled) dba.is_bundled = true;
  if (formData.processing_offered) dba.processing_offered = formData.processing_offered;
  if (formData.monthly_volume) dba.monthly_volume = formData.monthly_volume;
  if (formData.highest_ticket) dba.highest_ticket = formData.highest_ticket;
  if (formData.average_tickets !== '' && formData.average_tickets != null) {
    dba.average_ticket = formData.average_tickets;
  }
  if (formData.fns_number_for_ebt) dba.fns_number_for_ebt = formData.fns_number_for_ebt;
  if (formData.dba_address) dba.address = formData.dba_address;
  if (formData.ssn) dba.ssn = formData.ssn;
  if (formData.ein) dba.ein = formData.ein;

  const legal_information = {};
  if (formData.legal_business_name) legal_information.name = formData.legal_business_name;
  if (formData.legal_business_phone) legal_information.phone = formData.legal_business_phone;
  if (formData.legal_business_address) legal_information.address = formData.legal_business_address;
  if (formData.legal_business_email) legal_information.email = formData.legal_business_email;

  const merchant = {};
  if (formData.federal_tax_id) merchant.federal_tax_id = formData.federal_tax_id;
  putNum(merchant, 'business_years', formData.business_years);
  putNum(merchant, 'business_months', formData.business_months);
  if (formData.business_category) merchant.business_category = formData.business_category;
  putNum(merchant, 'avg_month_sales', formData.avg_month_sales);

  const bank_account = {};
  if (formData.bank_name) bank_account.bank_name = formData.bank_name;
  if (formData.routing_number) bank_account.routing_number = formData.routing_number;
  if (formData.account_number) bank_account.account_number = formData.account_number;

  const equipmentRow = {};
  if (formData.equipment_type_id) equipmentRow.equipment_type_id = formData.equipment_type_id;
  if (formData.manufacturer_id) equipmentRow.manufacturer_id = formData.manufacturer_id;
  if (formData.equipment_model_id) equipmentRow.equipment_model_id = formData.equipment_model_id;
  if (formData.lease_id) equipmentRow.lease_id = formData.lease_id;
  if (formData.merchant_service_provider_id) {
    equipmentRow.merchant_service_provider_id = formData.merchant_service_provider_id;
  }

  const payload = { ...base };

  if (Object.keys(dba).length) payload.dba = dba;
  if (Object.keys(legal_information).length) payload.legal_information = legal_information;
  if (Object.keys(merchant).length) payload.merchant = merchant;
  if (Object.keys(bank_account).length) payload.bank_account = bank_account;
  if (Object.keys(equipmentRow).length) payload.equipment = [equipmentRow];

  if (formData.additional_note !== '' && formData.additional_note != null) {
    payload.additional_note = formData.additional_note;
  }

  if (Array.isArray(formData.attachment_ids)) {
    payload.attachments = [...formData.attachment_ids];
  }

  const offerServices = serializeOfferedServices(formData.offer_services);
  if (offerServices.length) {
    payload.offer_services = offerServices;
  }

  if (
    isFollowUpStatusSelected(statusOptions, formData.status_id) &&
    formData.follow_up_date_time
  ) {
    payload.follow_up_date_time = formData.follow_up_date_time;
  }

  if (isLiveTransferStatusSelected(statusOptions, formData.status_id) && formData.closer_uuid) {
    payload.assignees = [formData.closer_uuid];
  }

  return payload;
}

const CreateLead = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { user, hasPermission } = useAuth();
  const canViewList = hasPermission('user-create');
  const isEditMode = Boolean(id);
  const isViewMode = window.location.pathname.includes('/view/') && !window.location.pathname.includes('/edit/');
  const isCreateMode = !id;
  const isCloserRole = isCloserUser(user);
  const importPrefillAppliedRef = useRef(false);

  const [formData, setFormData] = useState({
    dba_name: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    status_id: '',
    follow_up_date_time: '',
    source_id: '',
    brand_id: '',
    country_id: '',
    assignee_ids: user?.id ? [user.id] : [],
    submission_date: '',
    legal_business_name: '',
    db_name: '',
    federal_tax_id: '',
    ssn: '',
    ein: '',
    business_years: '',
    business_months: '',
    business_category: '',
    monthly_volume: '',
    equipment_type_id: '',
    manufacturer_id: '',
    equipment_model_id: '',
    personal_phone: '',
    legal_business_phone: '',
    legal_business_address: '',
    legal_business_email: '',
    monthly_lease: '',
    is_bundled: false,
    processing_offered: '',
    avg_month_sales: '',
    highest_ticket: '',
    average_tickets: '',
    bank_name: '',
    routing_number: '',
    account_number: '',
    fns_number_for_ebt: '',
    dba_address: '',
    additional_note: '',
    lease_id: '',
    merchant_service_provider_id: '',
    attachment_ids: [],
    attachments: [],
    closer_uuid: '',
    import_business_id: '',
    offer_services: [createEmptyOfferedService()],
  });

  const [errors, setErrors] = useState({});
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [closerOptions, setCloserOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [attachmentsHydrate, setAttachmentsHydrate] = useState(0);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch lead data after options are loaded
  useEffect(() => {
    if ((isEditMode || isViewMode) && optionsLoaded) {
      fetchLeadData();
    }
  }, [isEditMode, isViewMode, optionsLoaded]);

  // Default status + brand when creating a new lead (options from API).
  // Closer role: status is hidden and forced to "Follow Up (C)".
  useEffect(() => {
    if (!optionsLoaded || isEditMode || isViewMode) return;
    const defaultStatus = isCloserRole
      ? findFollowUpCStatus(statusOptions)
      : statusOptions.find((s) => (s.name || '').toLowerCase() === 'pending');
    const reviveBrand = brandOptions.find(
      (b) =>
        (b.name || '').toLowerCase().replace(/\s/g, '') === 'revivepay' ||
        (b.name || '').toLowerCase().includes('revive pay')
    );
    if (defaultStatus || reviveBrand) {
      setFormData((prev) => ({
        ...prev,
        ...(defaultStatus && {
          status_id: defaultStatus.id,
          ...(isCloserRole && !prev.follow_up_date_time
            ? { follow_up_date_time: toDatetimeLocalValue(new Date()) }
            : {}),
        }),
        ...(reviveBrand && { brand_id: reviveBrand.id }),
      }));
    }
  }, [optionsLoaded, isEditMode, isViewMode, statusOptions, brandOptions, isCloserRole]);

  // Prefill from import business transfer (Lead Gen → Transferred status)
  useEffect(() => {
    if (!optionsLoaded || !isCreateMode || importPrefillAppliedRef.current) return;
    const prefill = location.state?.importBusinessPrefill;
    if (!prefill) return;

    importPrefillAppliedRef.current = true;

    const sourceName = (location.state?.importBusinessSource || '').trim();
    let matchedSourceId = '';
    if (sourceName && sourceOptions.length) {
      const normalizedSource = sourceName.toLowerCase();
      const match = sourceOptions.find(
        (s) => (s.name || '').toLowerCase() === normalizedSource
      );
      if (match) matchedSourceId = match.id;
    }

    const followUpC = isCloserRole ? findFollowUpCStatus(statusOptions) : null;
    setFormData((prev) => ({
      ...prev,
      ...prefill,
      // Closer role always keeps Follow Up (C); other roles use prefill/default status
      status_id: followUpC?.id || prefill.status_id || prev.status_id,
      brand_id: prefill.brand_id || prev.brand_id,
      source_id: prefill.source_id || matchedSourceId || prev.source_id,
      assignee_ids: prefill.assignee_ids?.length ? prefill.assignee_ids : prev.assignee_ids,
      ...(followUpC && !prev.follow_up_date_time && !prefill.follow_up_date_time
        ? { follow_up_date_time: toDatetimeLocalValue(new Date()) }
        : {}),
    }));
  }, [optionsLoaded, isCreateMode, location.state, sourceOptions, statusOptions, isCloserRole]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchStatusData(),
        fetchSourceLead(),
        fetchBrandsLeads(),
        fetchCountries(),
        fetchClosers(),
      ]);
      setOptionsLoaded(true);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      setDataLoaded(false);
      const response = await dispatch(getLeadById(id)).unwrap();

      if (response && response.success) {
        const leadData = response.data ?? response;
        let assigneeIds = [];
        console.log(assigneeIds, 'assigneeIds checking');

        // if (leadData?.assignees?.id) {
        //   assigneeIds = [leadData?.assignees?.[0].id];
        // }
        if (Array.isArray(leadData?.assignees) && leadData.assignees.length > 0) {
          assigneeIds = leadData.assignees.map(assignee => assignee.id);
        }
        try {
          const assignedRes = await dispatch(getLeadAssignedUsers(id)).unwrap();
          const logs = assignedRes?.data?.data?.status_logs ?? assignedRes?.data?.status_logs ?? assignedRes?.status_logs ?? [];
          const ids = [...new Set(logs.map((s) => s.assignee?.id ?? s.assignee?.uuid ?? s.user_id).filter(Boolean))];
          if (ids.length) assigneeIds = ids;
        } catch (_) {
          // keep assigneeIds from last_status_log if fetch fails
        }
        setFormData({
          ...mapLeadApiToForm(leadData),
          assignee_ids: assigneeIds.length ? [assigneeIds[0]] : [],
          attachment_ids: extractLeadAttachmentIds(leadData),
          attachments: Array.isArray(leadData.attachments) ? leadData.attachments : [],
        });
        setAttachmentsHydrate((x) => x + 1);
        setDataLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching lead data:', error);
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusData = async () => {
    try {
      const params = { 'conditions[model]': 'Lead' };
      const response = await dispatch(getLeadsStatus(params)).unwrap();
      const rawList = extractList(response);
      const statusList = (Array.isArray(rawList) ? rawList : []).map(status => ({
        id: status.id,
        name: status.name
      }));
      setStatusOptions(statusList);
    } catch (error) {
      console.error('Error fetching status data:', error);
    }
  };

  const fetchSourceLead = async () => {
    try {
      const response = await dispatch(getLeadSources()).unwrap();
      const rawList = extractList(response);
      const getSources = (Array.isArray(rawList) ? rawList : []).map(source => ({
        id: source?.id,
        name: source?.name
      }));
      setSourceOptions(getSources);
    } catch (error) {
      console.error('Error fetching source data:', error);
    }
  }

  const fetchBrandsLeads = async () => {
    try {
      const response = await dispatch(getLeadBrands()).unwrap();
      const rawList = extractList(response);
      const getbrandLeads = (Array.isArray(rawList) ? rawList : []).map(brand => ({
        id: brand?.id,
        name: brand?.name
      }));
      setBrandOptions(getbrandLeads);
    } catch (error) {
      console.error('Error fetching brands data:', error);
    }
  };

  const fetchClosers = async () => {
    try {
      const { data } = await api.get(USERS.BY_ROLE('closer'));
      const rawList = data?.data?.list ?? data?.data ?? data?.list ?? [];
      const list = (Array.isArray(rawList) ? rawList : []).map((u) => ({
        id: u?.id,
        uuid: u?.uuid,
        name: u?.name || `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim(),
      }));
      setCloserOptions(list);
    } catch (error) {
      console.error('Error fetching closers:', error);
      setCloserOptions([]);
    }
  };

  const fetchCountries = async () => {
    try {
      let rawList = getCachedCountries();
      if (!Array.isArray(rawList) || rawList.length === 0) {
        const { data } = await api.get(LOOKUP.COUNTRIES);
        rawList = data?.data?.list ?? data?.list ?? extractList(data);
        setCachedCountries(rawList);
        const usId = findUnitedStatesId(rawList);
        if (usId != null) setCachedUsCountryId(usId);
      }
      setCountryOptions(
        (Array.isArray(rawList) ? rawList : []).map((c) => ({
          id: c?.id,
          name: c?.name,
        }))
      );
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountryOptions([]);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Helper function to get display value for selects in view mode
  const getDisplayValue = (options, selectedId) => {
    const selectedOption = options.find(option => option.id === selectedId);
    return selectedOption ? selectedOption.name : 'N/A';
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.dba_name.trim()) newErrors.dba_name = 'DBA Name is required';
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.status_id && !(Boolean(id) && !isViewMode)) {
      newErrors.status_id = 'Status is required';
    }
    if (
      (isCreateMode || isViewMode) &&
      !(isCreateMode && isCloserRole) &&
      isFollowUpStatusSelected(statusOptions, formData.status_id) &&
      !formData.follow_up_date_time
    ) {
      newErrors.follow_up_date_time = 'Follow-up date & time is required';
    }
    if (!formData.source_id) newErrors.source_id = 'Source is required';
    if (!formData.brand_id) newErrors.brand_id = 'Brand is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        setSubmitting(true);

        const merged = buildLeadSubmitPayload(formData, statusOptions);

        let response;
        if (isEditMode) {
          response = await dispatch(updateLead({ id, data: merged })).unwrap();
        } else {
          response = await dispatch(createLead(merged)).unwrap();
        }

        if (response.success) {
          navigate('/leads');
        } else {
          console.error(`Failed to ${isEditMode ? 'update' : 'create'} lead:`, response.message);
        }
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'creating'} lead:`, error);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    navigate('/leads');
  };

  const handleEdit = () => {
    navigate(`/leads/edit/${id}`);
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length > 10) value = value.slice(0, 10);
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  // All fields are disabled in view mode or during loading
  const isDisabled = isViewMode || loading;

  // Skeleton loading component for form fields
  const renderSkeletonField = (width = '100%') => (
    <Skeleton variant="rectangular" width={width} height={40} animation="wave" />
  );

  const renderSkeletonSelect = () => (
    <Skeleton variant="rectangular" width="100%" height={56} animation="wave" />
  );

  const renderSkeletonTextArea = () => (
    <Skeleton variant="rectangular" width="100%" height={80} animation="wave" />
  );

  // View mode styling
  const viewModeTitleStyle = {
    color: 'text.secondary',
    fontWeight: 600,
    mb: 1
  };

  const viewModeFieldStyle = {
    p: 1.5,
    border: '1px solid',
    borderColor: 'grey.300',
    borderRadius: 1,
    minHeight: '40px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'grey.50',
    color: 'text.primary'
  };

  const viewModeTextAreaStyle = {
    ...viewModeFieldStyle,
    minHeight: '80px',
    alignItems: 'flex-start'
  };

  const capitalizeWords = (text) => {
    if (!text) return '';
    return text.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header with Skeleton */}
        <Box sx={{ mb: 4 }}>
          {!loading && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleCancel}
              sx={{ mb: 2 }}
              disabled={submitting}
            >
              Back to Leads
            </Button>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {loading ? (
              <Skeleton variant="text" width={200} height={40} animation="wave" />
            ) : (
              <Typography
                variant="h4"
                sx={{
                  color: isViewMode ? 'text.secondary' : 'blue.600',
                  fontWeight: 600,
                  mb: 1,
                  opacity: isViewMode ? 0.7 : 1
                }}
              >
                {isViewMode ? 'View Lead' : isEditMode ? 'Edit Lead' : 'Add Lead'}
              </Typography>
            )}

            {isViewMode && !loading && (
              <Button
                variant="contained"
                onClick={handleEdit}
                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                Edit Lead
              </Button>
            )}
            {isViewMode && loading && (
              <Skeleton variant="rectangular" width={120} height={36} animation="wave" />
            )}
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Card sx={{
            boxShadow: isViewMode ? 1 : 2,
            border: isViewMode ? '1px solid' : 'none',
            borderColor: isViewMode ? 'grey.300' : 'transparent'
          }}>
            <CardContent sx={{ p: 4 }}>
              {!isViewMode && !loading && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Fields marked with <Box component="span" sx={{ color: 'error.main' }}>*</Box> are required.
                </Typography>
              )}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonField()
                  ) : isViewMode ? (
                    <>
                      <Typography variant="subtitle1" sx={viewModeTitleStyle}>
                        DBA Name
                      </Typography>
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {formData.dba_name || 'N/A'}
                      </Typography>
                    </>
                  ) : (
                    <TextField
                      label="DBA Name"
                      fullWidth
                      required
                      placeholder="Enter DBA name (Doing Business As)"
                      value={formData.dba_name}
                      onChange={handleChange('dba_name')}
                      error={!!errors.dba_name}
                      helperText={errors.dba_name}
                      disabled={isDisabled}
                    />
                  )}
                </Grid>
                {/* Name Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonField()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {formData.name || 'N/A'}
                    </Typography>
                  ) : (
                    <TextField
                      label="Customer Name"
                      fullWidth
                      required
                      placeholder="Customer Name"
                      value={formData.name}
                      onChange={handleChange('name')}
                      error={!!errors.name}
                      helperText={errors.name}
                      disabled={isDisabled}
                    />
                  )}
                </Grid>

                {/* Email Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonField()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {formData.email || 'N/A'}
                    </Typography>
                  ) : (
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      required
                      placeholder="name@example.com"
                      value={formData.email}
                      onChange={handleChange('email')}
                      error={!!errors.email}
                      helperText={errors.email}
                      disabled={isDisabled}
                    />
                  )}
                </Grid>

                {/* Phone Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonField()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {formData.phone || 'N/A'}
                    </Typography>
                  ) : (
                    <TextField
                      fullWidth
                      label="Phone"
                      type='tel'
                      required
                      placeholder="1234567890"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      disabled={isDisabled}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 15
                      }}
                    />
                  )}
                </Grid>

                {/* Country Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonSelect()
                  ) : isViewMode ? (
                    <>
                      <Typography variant="subtitle1" sx={viewModeTitleStyle}>
                        Country
                      </Typography>
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {getDisplayValue(countryOptions, formData.country_id)}
                      </Typography>
                    </>
                  ) : (
                    <Autocomplete
                      options={countryOptions}
                      getOptionLabel={(option) => option.name || ""}
                      value={countryOptions.find(
                        (country) => country.id === formData.country_id
                      ) || null}
                      onChange={(event, newValue) => {
                        setFormData({
                          ...formData,
                          country_id: newValue ? newValue.id : "",
                        });
                      }}
                      disabled={isDisabled}
                      renderInput={(params) => (
                        <TextField {...params} label="Select country" fullWidth />
                      )}
                    />
                  )}
                </Grid>

                {/* Source Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonSelect()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {capitalizeWords(getDisplayValue(sourceOptions, formData.source_id))}
                    </Typography>
                  ) : (
                    <Autocomplete
                      options={sourceOptions}
                      getOptionLabel={(option) => capitalizeWords(option.name || "")}
                      value={
                        sourceOptions.find(
                          (source) => source.id === formData.source_id
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setFormData({
                          ...formData,
                          source_id: newValue ? newValue.id : "",
                        });
                        if (errors.source_id) {
                          setErrors((prev) => ({ ...prev, source_id: '' }));
                        }
                      }}
                      disabled={isDisabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select source"
                          required
                          error={!!errors.source_id}
                          helperText={errors.source_id}
                          fullWidth
                        />
                      )}
                    />
                  )}
                </Grid>

                {/* Address Field - NEW FIELD ADDED */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonField()
                  ) : isViewMode ? (
                    <>
                      <Typography variant="subtitle1" sx={viewModeTitleStyle}>
                        Address
                      </Typography>
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {formData.address || 'N/A'}
                      </Typography>
                    </>
                  ) : (
                    <TextField
                      fullWidth
                      label="Address"
                      placeholder="Enter complete address"
                      value={formData.address}
                      onChange={handleChange('address')}
                      disabled={isDisabled}
                      multiline
                      rows={3}
                    />
                  )}
                </Grid>

                {/* Description Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonTextArea()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeTextAreaStyle}>
                      {formData.description || 'N/A'}
                    </Typography>
                  ) : (
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={3}
                      placeholder="Enter description"
                      value={formData.description}
                      onChange={handleChange('description')}
                      disabled={isDisabled}
                    />
                  )}
                </Grid>

                {/* Brand Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonSelect()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {capitalizeWords(getDisplayValue(brandOptions, formData.brand_id))}
                    </Typography>
                  ) : (
                    <Autocomplete
                      options={brandOptions}
                      getOptionLabel={(option) => capitalizeWords(option.name || "")}
                      value={
                        brandOptions.find(
                          (brand) => brand.id === formData.brand_id
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setFormData({
                          ...formData,
                          brand_id: newValue ? newValue.id : "",
                        });
                        if (errors.brand_id) {
                          setErrors((prev) => ({ ...prev, brand_id: '' }));
                        }
                      }}
                      disabled={isDisabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select brand"
                          required
                          error={!!errors.brand_id}
                          helperText={errors.brand_id}
                          fullWidth
                        />
                      )}
                    />
                  )}
                </Grid>

                {/* Status Field — create + view only (hidden on edit; use Change Status from detail/list).
                    Closer role: status is hidden on create and forced to Follow Up (C). */}
                {((isCreateMode && !isCloserRole) || isViewMode) && (
                <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonSelect()
                    ) : isViewMode ? (
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {capitalizeWords(getDisplayValue(statusOptions, formData.status_id))}
                      </Typography>
                    ) : (
                      <Autocomplete
                        options={statusOptions}
                        getOptionLabel={(option) => capitalizeWords(option.name || "")}
                        value={
                          statusOptions.find(
                            (status) => status.id === formData.status_id
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          const newStatusId = newValue ? newValue.id : '';
                          const isFollowUp = isFollowUpStatusSelected(
                            statusOptions,
                            newStatusId
                          );
                          setFormData((prev) => ({
                            ...prev,
                            status_id: newStatusId,
                            ...(!isFollowUp
                              ? { follow_up_date_time: '' }
                              : !prev.follow_up_date_time
                              ? { follow_up_date_time: toDatetimeLocalValue(new Date()) }
                              : {}),
                          }));
                          if (errors.status_id || errors.follow_up_date_time) {
                            setErrors((prev) => ({
                              ...prev,
                              status_id: '',
                              ...(!isFollowUp ? { follow_up_date_time: '' } : {}),
                            }));
                          }
                        }}
                        disabled={isDisabled}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select status"
                            required
                            error={!!errors.status_id}
                            helperText={errors.status_id}
                            fullWidth
                          />
                        )}
                      />
                    )}
                </Grid>
                )}

                {((isCreateMode && !isCloserRole) || isViewMode) &&
                  isFollowUpStatusSelected(statusOptions, formData.status_id) && (
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonSelect()
                    ) : isViewMode ? (
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {formData.follow_up_date_time
                          ? new Date(
                              String(formData.follow_up_date_time).replace(' ', 'T')
                            ).toLocaleString()
                          : 'N/A'}
                      </Typography>
                    ) : (
                      <TextField
                        fullWidth
                        label="Follow-up date & time"
                        type="datetime-local"
                        required
                        name="follow_up_date_time"
                        value={formData.follow_up_date_time}
                        onChange={handleChange('follow_up_date_time')}
                        disabled={isDisabled}
                        error={!!errors.follow_up_date_time}
                        helperText={errors.follow_up_date_time}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  </Grid>
                )}
{/* 
                {isLiveTransferStatusSelected(statusOptions, formData.status_id) && (
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonSelect()
                    ) : isViewMode ? (
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {closerOptions.find((c) => c.uuid === formData.closer_uuid)?.name || 'N/A'}
                      </Typography>
                    ) : (
                      <Autocomplete
                        options={closerOptions}
                        getOptionLabel={(option) => option.name || ''}
                        value={closerOptions.find((c) => c.uuid === formData.closer_uuid) || null}
                        onChange={(event, newValue) => {
                          setFormData((prev) => ({ ...prev, closer_uuid: newValue ? newValue.uuid : '' }));
                        }}
                        disabled={isDisabled}
                        renderInput={(params) => (
                          <TextField {...params} label="Select closer" fullWidth />
                        )}
                      />
                    )}
                  </Grid>
                )} */}

                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonField()
                  ) : isViewMode ? (
                    <>
                      <Typography variant="subtitle1" sx={viewModeTitleStyle}>
                        Average tickets
                      </Typography>
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {formData.average_tickets || 'N/A'}
                      </Typography>
                    </>
                  ) : (
                    <TextField
                      fullWidth
                      label="Average tickets"
                      placeholder="e.g. $250 or typical ticket size"
                      value={formData.average_tickets ?? ''}
                      onChange={handleChange('average_tickets')}
                      disabled={isDisabled}
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <LeadAdditionalDetailsSection
                    formData={formData}
                    setFormData={setFormData}
                    disabled={isDisabled}
                    isViewMode={isViewMode}
                    leadId={isEditMode || isViewMode ? id : null}
                    attachmentsHydrateToken={attachmentsHydrate}
                    showOfferServices={Boolean(id) && !isViewMode}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Action Buttons with Skeletons */}
          {!isViewMode && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              {loading ? (
                <>
                  <Skeleton variant="rectangular" width={120} height={36} animation="wave" />
                  <Skeleton variant="rectangular" width={120} height={36} animation="wave" />
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{ minWidth: '120px' }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ minWidth: '120px', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                    disabled={submitting}
                  >
                    {submitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Lead' : 'Create Lead')}
                  </Button>
                </>
              )}
            </Box>
          )}

          {/* Back Button for View Mode with Skeleton */}
          {isViewMode && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              {loading ? (
                <Skeleton variant="rectangular" width={120} height={36} animation="wave" />
              ) : (
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ minWidth: '120px' }}
                >
                  Back to Leads
                </Button>
              )}
            </Box>
          )}
        </form>
      </Container>
    </PageLayout>
  );
};

export default CreateLead;